// Toast.tsx — single Radix Toast.Root with semantic variant styling.
//
// Plan ref: 06-06-PLAN-cmd-k-toast-empty-loading-error.md §Task 2 step 1
//          + 06-RESEARCH-theme-tokens.md §Per-Component Token Mapping (Toast).
//
// Variants:
//   success — bg-surface-2 + border-success-9 + CheckCircle icon
//   error   — bg-surface-2 + border-danger-9  + XCircle icon
//   info    — bg-surface-2 + border-info-9    + Info icon
//   warning — bg-surface-2 + border-warning-9 + TriangleAlert icon
//
// Auto-dismiss timing:
//   success / info  → 5s  (Radix Toast duration)
//   warning         → 7s
//   error           → ∞   (Number.POSITIVE_INFINITY → no auto-dismiss)
//
// a11y:
//   error variant uses Radix Toast.Root type='foreground' which sets
//   role=alert + aria-live=assertive (per Radix Toast contract).
//   success/info/warning use type='background' → role=status + polite.
//
// Animation: Radix swipe + open/close states are styled with the
// data-state CSS attributes, paired with our motion role tokens. We do
// NOT wrap Radix Toast.Root with motion.* via asChild — combining
// AnimatePresence with Radix's own remove timing produces double-mounted
// nodes that the unit tests cannot detect as removed. The opacity/translate
// transitions on data-state achieve the same Linear-feel without that gap.

import * as RToast from '@radix-ui/react-toast';
import {
  Icon,
  CheckCircle,
  XCircle,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from '@/components/Icon';
import { cn } from '@/lib/cn';
import type { ToastVariant } from './useToast';

export interface ToastEntry {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface ToastProps {
  entry: ToastEntry;
  onDismiss: (id: string) => void;
}

const VARIANT_CLASS: Record<ToastVariant, string> = {
  success: 'border-success-9',
  error: 'border-danger-9',
  info: 'border-info-9',
  warning: 'border-warning-9',
};

const VARIANT_ICON: Record<ToastVariant, LucideIcon> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: TriangleAlert,
};

const VARIANT_ICON_COLOR: Record<ToastVariant, string> = {
  success: 'text-success-11',
  error: 'text-danger-11',
  info: 'text-info-11',
  warning: 'text-warning-11',
};

const VARIANT_DURATION_MS: Record<ToastVariant, number> = {
  success: 5_000,
  info: 5_000,
  warning: 7_000,
  // Errors stay visible until explicitly dismissed (Plan §truths line 4).
  error: Number.POSITIVE_INFINITY,
};

export function Toast({ entry, onDismiss }: ToastProps): JSX.Element {
  const isError = entry.variant === 'error';

  return (
    <RToast.Root
      data-testid="toast-root"
      data-variant={entry.variant}
      type={isError ? 'foreground' : 'background'}
      duration={VARIANT_DURATION_MS[entry.variant]}
      // Radix Toast.Root has no role attribute by default; the announce
      // node is rendered separately. We promote role=alert + aria-live for
      // errors so screen readers and our test harness can detect them
      // directly on the visible toast container.
      role={isError ? 'alert' : undefined}
      aria-live={isError ? 'assertive' : undefined}
      onOpenChange={(open) => {
        if (!open) onDismiss(entry.id);
      }}
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-lg border bg-surface-2 px-4 py-3 shadow-card',
        'min-w-[280px] max-w-[420px]',
        // Linear-feel transitions on Radix's data-state. Compositor-only
        // properties (opacity + translate) keep INP under 200ms.
        'transition-all duration-fast ease-out-soft',
        'data-[state=open]:translate-y-0 data-[state=open]:opacity-100',
        'data-[state=closed]:translate-y-2 data-[state=closed]:opacity-0',
        'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
        'data-[swipe=end]:translate-x-[calc(100%+1rem)]',
        VARIANT_CLASS[entry.variant],
      )}
    >
      <Icon
        icon={VARIANT_ICON[entry.variant]}
        size={16}
        data-testid={`toast-icon-${entry.variant}`}
        className={cn('mt-0.5 shrink-0', VARIANT_ICON_COLOR[entry.variant])}
      />
      <div className="flex-1 min-w-0">
        <RToast.Title className="text-sm font-medium text-fg-default">{entry.title}</RToast.Title>
        {entry.description ? (
          <RToast.Description className="mt-0.5 text-xs text-fg-muted">
            {entry.description}
          </RToast.Description>
        ) : null}
      </div>
      <RToast.Close
        aria-label="Dismiss"
        className="tap-target rounded p-1 text-fg-muted hover:bg-overlay-hover hover:text-fg-default"
      >
        <Icon icon={X} size={16} />
      </RToast.Close>
    </RToast.Root>
  );
}
